import { useEffect } from 'react';

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iP(ad|hone|od)/i.test(navigator.userAgent);
}

/**
 * Lock body scroll when `enabled` is true.
 * Uses a dataset-based stack counter so multiple callers can lock concurrently.
 * On iOS it uses `position: fixed` to reliably prevent background scroll and
 * preserves/restores the scroll position.
 */
export function useLockBodyScroll(enabled: boolean) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    const html = document.documentElement;
    if (!body || !html) return;

    if (!enabled) return;

    const prevCount = Number(body.dataset.scrollLockCount || '0');
    body.dataset.scrollLockCount = String(prevCount + 1);

    if (prevCount === 0) {
      // store previous styles
      body.dataset.scrollLockPrev = body.style.overflow || '';
      html.dataset.scrollLockPrev = html.style.overflow || '';

      if (isIOS()) {
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
        body.dataset.scrollLockScrollY = String(scrollY);
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';
      } else {
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
      }
    }

    return () => {
      try {
        const currentCount = Number(body.dataset.scrollLockCount || '0');
        const next = Math.max(currentCount - 1, 0);
        body.dataset.scrollLockCount = String(next);

        if (next === 0) {
          // restore previous styles
          const restoreBody = body.dataset.scrollLockPrev || '';
          const restoreHtml = html.dataset.scrollLockPrev || '';

          if (isIOS()) {
            const prevScroll = Number(body.dataset.scrollLockScrollY || '0');
            body.style.position = '';
            body.style.top = '';
            body.style.width = '';
            window.scrollTo(0, prevScroll);
            delete body.dataset.scrollLockScrollY;
          } else {
            body.style.overflow = restoreBody;
            html.style.overflow = restoreHtml;
          }

          delete body.dataset.scrollLockPrev;
          delete html.dataset.scrollLockPrev;
        }
      } catch (e) {
        // ignore cleanup errors
      }
    };
  }, [enabled]);
}

export default useLockBodyScroll;
